import { useCallback, useEffect, useState } from "react";
import {
  useEventListener,
  useTimeAxisContext,
} from "../../../../utils/customHooks/genericHooks";
import { useTimelineTooltipStore } from "../TimelineTooltip/timelineTooltipStore";
import { useSelector } from "store/useSelector";
import { debounce, isEqual } from "lodash";
import { useTimelinesStore } from "../TimelineCardControls/timelinesStore";

/** a single mousemove handler for a single timeline
 * detects mousemove/click on timeline,
 * and shows the tooltip
 */
export function MouseMoveHandler({
  timelineSelector,
  timelineIdx,
}: {
  timelineSelector: string;
  timelineIdx: number;
}) {
  const [timelineElement, setTimelineElement] = useState<HTMLElement | null>(
    document.querySelector<HTMLElement>(timelineSelector)
  );
  useEffect(() => {
    setTimelineElement(document.querySelector<HTMLElement>(timelineSelector));
  }, [timelineSelector]);

  return (
    <>
      <ClickHandler
        {...{
          timelineIdx,
          timelineSelector,
          timelineElement,
        }}
      />
      <MousemoveEventHandler {...{ timelineIdx, timelineElement }} />
      <MouseleaveHandler {...{ timelineElement }} />
    </>
  );
}

function ClickHandler({ timelineIdx, timelineElement }) {
  const [{ selectedTimelineIdx }, setTimelineTooltipState] =
    useTimelineTooltipStore();

  const { handleClickSegment } = useMouseHandlers();

  useEventListener(
    "click",
    event => {
      const shouldSelect =
        timelineIdx === Number(event.target.dataset.timelineIdx);

      setTimelineTooltipState(p => ({
        ...p,
        selectedTimelineIdx: shouldSelect
          ? event.target.dataset.timelineIdx ||
            event.target.dataset.timelineIdx === 0
            ? Number(event.target.dataset.timelineIdx)
            : selectedTimelineIdx
          : null,
      }));
      if (shouldSelect) {
        handleClickSegment(event);
      }
    },
    timelineElement
  );
  return null;
}
function MousemoveEventHandler({ timelineIdx, timelineElement }) {
  const [{ isModalPinned }, setTimelineTooltipState] =
    useTimelineTooltipStore();
  const { handleMouseMoveSegment } = useMouseHandlers();

  // set up mousemove/click listeners on the timeline
  useEventListener(
    "mousemove",
    debounce(event => {
      if (isModalPinned) {
        return;
      }

      const shouldSelect =
        timelineIdx === Number(event.target.dataset.timelineIdx);

      // do this the imperative way, so we don't have to hook up every ProductionSegment to the same store (would cause re-renders)

      unselectAllSegments();

      if (!shouldSelect) {
        return;
      }

      // select the new segment
      selectSegment(event.target.dataset.segmentId);

      setTimelineTooltipState(p => ({
        ...p,
        selectedTimelineIdx: shouldSelect
          ? event.target.dataset.timelineIdx === undefined
            ? null
            : Number(event.target.dataset.timelineIdx)
          : null,
      }));
      if (shouldSelect) {
        handleMouseMoveSegment(event);
      }
    }, 2),
    timelineElement
  );

  return null;
}

function MouseleaveHandler({ timelineElement }) {
  const [{ isModalPinned }, setTimelineTooltipStore] =
    useTimelineTooltipStore();
  // clear it on mouseleave
  useEventListener(
    "mouseleave",
    () => {
      if (!isModalPinned) {
        setTimelineTooltipStore(p => ({
          ...p,
          modal: null,
          machemetryId: null,
          selectedProdSegment: null,
          selectedTimelineIdx: null,
        }));
        unselectAllSegments();
      }
    },
    timelineElement
  );
  return null;
}

/** on mousemove/click, show the tooltip */
function useMouseHandlers() {
  const { showTimelineTooltipOnHover } = useTimelinesStore();
  const [{ isModalPinned }] = useTimelineTooltipStore();
  const { scaleTime } = useTimeAxisContext();
  const [, setTimelineTooltipStore] = useTimelineTooltipStore();
  const timeAxisSVGRef = document.getElementById("timeAxisSvg");
  const isPopperOpen = useSelector(
    state => state.retrotag.isRetrotagPopperOpen
  );
  const retrotagProcessId = useSelector(
    state => state.retrotag.retrotagProcessId
  );

  const handleMouseMoveSegment = useCallback(
    (event, shouldPin = false) => {
      // grab the segment data from the click event
      const timelineIdx = event.target.dataset.timelineIdx;
      const machemetryId = event.target.dataset.machemetryId;
      const segment = JSON.parse(event.target.dataset.segment || {});

      const isRetrotagPopperOpen = Boolean(
        isPopperOpen && machemetryId && retrotagProcessId === machemetryId
      );

      if (
        (isModalPinned && !shouldPin) ||
        !showTimelineTooltipOnHover ||
        !timeAxisSVGRef ||
        isRetrotagPopperOpen
      ) {
        return;
      }

      const { left: x } =
        timeAxisSVGRef.getBoundingClientRect(); /* ie11 doesn't support boundingRect.x, only .left */
      const axisMouseXPosition = event.pageX - x;

      const t = scaleTime.invert(axisMouseXPosition).getTime();

      const nextModal = {
        t,
        clickIndex: timelineIdx,
      };

      setTimelineTooltipStore(p => {
        return {
          ...p,
          machemetryId,
          modal: nextModal,
          selectedProdSegment:
            // prevents re-rendering TimelineTooltipTagDisplay when mousing over the same segment
            isEqual(segment, p.selectedProdSegment)
              ? p.selectedProdSegment
              : segment,
          ...(shouldPin ? { isModalPinned: true } : {}),
        };
      });
    },
    [
      isPopperOpen,
      retrotagProcessId,
      isModalPinned,
      showTimelineTooltipOnHover,
      timeAxisSVGRef,
      scaleTime,
      setTimelineTooltipStore,
    ]
  );

  /** on click, show & pin the tooltip */
  const handleClickSegment = useCallback(
    event => {
      unselectAllSegments();
      const segmentId = event.target.dataset.segmentId;
      if (segmentId) {
        selectSegment(segmentId);
      }
      handleMouseMoveSegment(event, true);
    },
    [handleMouseMoveSegment]
  );

  return { handleClickSegment, handleMouseMoveSegment };
}

export function unselectAllSegments() {
  document
    .querySelectorAll(".production-segment-selected-border:not(.hidden)")
    .forEach(segment => {
      segment.classList.add("hidden");
    });
}

function selectSegment(segmentId: string) {
  document
    .querySelector(`.production-segment-selected-border-${segmentId}`)
    ?.classList.remove("hidden");
}
